/**
 * @fileoverview High-fidelity Live Preview component for FanMessages (Mensajes a la afición).
 * Accurately renders the 11 visual banner formats supporting custom backgrounds (transparent,
 * solid color, images, videos), typography styling, dynamic font auto-scaling, and side graphics/QRs.
 * 
 * @module components/Admin/Games/FanMessages/FanMessagePreview
 */

import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Resolves a local or relative media URL into a full playable/viewable path.
 * 
 * @function resolveMediaUrl
 * @param {string} mediaPath - Raw media string from database or upload.
 * @returns {string} Fully qualified URL.
 */
export const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
    return `${apiBaseUrl}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
};

/**
 * Format names and descriptive labels for the 11 visual layouts.
 */
export const FAN_MESSAGE_FORMATS = [
    {
        id: 1,
        name: 'FORMAT_1_LARGE_TEXT_SIDE_IMAGES',
        label: '1. Texto Grande + Logos Laterales',
        description: 'Texto principal centrado con logos o imágenes a ambos lados.',
        icon: 'bi-layout-three-columns',
        hasLeftImage: true,
        hasRightImage: true,
        hasSubtitle: false
    },
    {
        id: 2,
        name: 'FORMAT_2_LARGE_TEXT_ONLY',
        label: '2. Solo Texto Grande (Ancho Completo)',
        description: 'Texto principal de máximo impacto sin elementos laterales.',
        icon: 'bi-type-h1',
        hasLeftImage: false,
        hasRightImage: false,
        hasSubtitle: false
    },
    {
        id: 3,
        name: 'FORMAT_3_SUBTITLE_TOP_TEXT_BOTTOM_SIDE_IMAGES',
        label: '3. Subtítulo Arriba + Texto Abajo + Logos',
        description: 'Subtítulo superior (ej. "De: Juan"), texto abajo y logos laterales.',
        icon: 'bi-card-heading',
        hasLeftImage: true,
        hasRightImage: true,
        hasSubtitle: true,
        subtitlePosition: 'top'
    },
    {
        id: 4,
        name: 'FORMAT_4_SUBTITLE_BOTTOM_TEXT_TOP_SIDE_IMAGES',
        label: '4. Texto Arriba + Subtítulo/Firma Abajo + Logos',
        description: 'Texto principal arriba con bajada/firma abajo y logos laterales.',
        icon: 'bi-card-text',
        hasLeftImage: true,
        hasRightImage: true,
        hasSubtitle: true,
        subtitlePosition: 'bottom'
    },
    {
        id: 5,
        name: 'FORMAT_5_SUBTITLE_TOP_TEXT_BOTTOM_NO_IMAGES',
        label: '5. Subtítulo Arriba + Texto Abajo (Sin Logos)',
        description: 'Subtítulo arriba y texto abajo a ancho completo sin elementos laterales.',
        icon: 'bi-text-paragraph',
        hasLeftImage: false,
        hasRightImage: false,
        hasSubtitle: true,
        subtitlePosition: 'top'
    },
    {
        id: 6,
        name: 'FORMAT_6_SUBTITLE_BOTTOM_TEXT_TOP_NO_IMAGES',
        label: '6. Texto Arriba + Subtítulo Abajo (Sin Logos)',
        description: 'Texto principal arriba y subtítulo/nota abajo sin elementos laterales.',
        icon: 'bi-justify-left',
        hasLeftImage: false,
        hasRightImage: false,
        hasSubtitle: true,
        subtitlePosition: 'bottom'
    },
    {
        id: 7,
        name: 'FORMAT_7_QR_BOTH_SIDES_CENTER_TEXT',
        label: '7. Códigos QR en Ambos Lados + Texto',
        description: 'Códigos QR izquierdo y derecho para escaneo masivo con texto central.',
        icon: 'bi-qr-code-scan',
        hasLeftQr: true,
        hasRightQr: true,
        hasSubtitle: true,
        subtitlePosition: 'top'
    },
    {
        id: 8,
        name: 'FORMAT_8_QR_LEFT_CENTER_TEXT',
        label: '8. Código QR Izquierdo + Texto Central',
        description: 'Código QR dinámico a la izquierda con mensaje central.',
        icon: 'bi-qr-code',
        hasLeftQr: true,
        hasRightImage: false,
        hasSubtitle: true,
        subtitlePosition: 'top'
    },
    {
        id: 9,
        name: 'FORMAT_9_QR_RIGHT_CENTER_TEXT',
        label: '9. Texto Central + Código QR Derecho',
        description: 'Mensaje central con código QR de llamada a la acción a la derecha.',
        icon: 'bi-qr-code',
        hasLeftImage: false,
        hasRightQr: true,
        hasSubtitle: true,
        subtitlePosition: 'top'
    },
    {
        id: 10,
        name: 'FORMAT_10_IMAGE_LEFT_CENTER_TEXT',
        label: '10. Logo/Avatar Izquierdo + Texto Central',
        description: 'Imagen o foto a la izquierda con texto central expandido hacia la derecha.',
        icon: 'bi-person-badge',
        hasLeftImage: true,
        hasRightImage: false,
        hasSubtitle: true,
        subtitlePosition: 'top'
    },
    {
        id: 11,
        name: 'FORMAT_11_IMAGE_RIGHT_CENTER_TEXT',
        label: '11. Texto Central + Logo/Avatar Derecho',
        description: 'Texto central con logo de patrocinador o imagen a la derecha.',
        icon: 'bi-award',
        hasLeftImage: false,
        hasRightImage: true,
        hasSubtitle: true,
        subtitlePosition: 'top'
    }
];

/**
 * FanMessagePreview Component.
 * 
 * @function FanMessagePreview
 * @param {Object} props - Component properties.
 * @param {Object} props.message - Fan message data structure.
 * @param {string} [props.mode='modal'] - Display mode ('compact', 'modal', 'full').
 * @param {number} [props.customScale=1] - Scale multiplier for miniaturization.
 * @param {boolean} [props.showContainerFrame=true] - Whether to wrap in a simulated stadium frame.
 * @returns {React.ReactElement} Live preview element.
 */
export const FanMessagePreview = ({
    message = {},
    mode = 'modal',
    customScale = 1,
    showContainerFrame = true
}) => {
    const {
        formatId = 1,
        mainText = '¡BIENVENIDOS AL ESTADIO EL ENCANTO!',
        subtitleText = '',
        backgroundType = 'transparent',
        backgroundUrl = '',
        backgroundColor = 'rgba(15, 23, 42, 0.95)',
        leftImageUrl = '',
        leftQrUrl = '',
        rightImageUrl = '',
        rightQrUrl = '',
        textColor = '#FFFFFF',
        subtitleColor = '#FCE68A',
        fontFamily = 'Montserrat',
        fontSizePx = 36,
        offsetY = 0
    } = message;

    const currentFormatId = Number(formatId) >= 1 && Number(formatId) <= 11 ? Number(formatId) : 1;

    // Determine font family stack
    const getFontFamilyStack = (fontName) => {
        switch (fontName) {
            case 'Bangers':
                return '"Bangers", cursive, "Montserrat", sans-serif';
            case 'Oswald':
                return '"Oswald", "Montserrat", sans-serif';
            case 'Arial Black':
            case 'Impact':
                return '"Arial Black", Impact, "Montserrat", sans-serif';
            case 'Montserrat':
            default:
                return '"Montserrat", -apple-system, BlinkMacSystemFont, sans-serif';
        }
    };

    // Calculate background styling
    const getBackgroundStyle = () => {
        if (backgroundType === 'color' && backgroundColor) {
            return { background: backgroundColor };
        }
        if (backgroundType === 'image' && backgroundUrl) {
            return {
                backgroundImage: `url(${resolveMediaUrl(backgroundUrl)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat'
            };
        }
        if (backgroundType === 'transparent') {
            return {
                background: 'linear-gradient(90deg, rgba(15, 23, 42, 0.92) 0%, rgba(30, 41, 59, 0.95) 50%, rgba(15, 23, 42, 0.92) 100%)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
                backdropFilter: 'blur(8px)',
                borderTop: '2px solid rgba(255, 255, 255, 0.15)',
                borderBottom: '2px solid rgba(255, 255, 255, 0.15)'
            };
        }
        return {
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
        };
    };

    // Base scale factor depending on mode
    const getScaleMultiplier = () => {
        if (customScale && customScale !== 1) return customScale;
        if (mode === 'compact') return 0.55;
        if (mode === 'modal') return 0.85;
        return 1.0;
    };

    const scale = getScaleMultiplier();
    const effectiveFontSize = Math.max(14, Math.round((Number(fontSizePx) || 36) * scale));
    const effectiveSubtitleSize = Math.max(12, Math.round(effectiveFontSize * 0.55));
    const sideElementSize = Math.round(72 * scale);
    const qrPixelSize = Math.max(48, Math.round(64 * scale));
    const bannerHeight = Math.max(70, Math.round(110 * scale));

    // Resolve side elements
    const renderLeftElement = () => {
        // Formats with QR on left
        if (currentFormatId === 7 || currentFormatId === 8) {
            if (leftQrUrl) {
                return (
                    <div className="d-flex align-items-center justify-content-center p-1 bg-white rounded-3 shadow-sm flex-shrink-0">
                        <QRCodeSVG value={leftQrUrl} size={qrPixelSize} level="M" />
                    </div>
                );
            }
            if (leftImageUrl) {
                return (
                    <img
                        src={resolveMediaUrl(leftImageUrl)}
                        alt="Izquierda"
                        className="rounded-3 shadow-sm object-fit-contain flex-shrink-0"
                        style={{ width: `${sideElementSize}px`, height: `${sideElementSize}px`, background: 'rgba(255,255,255,0.08)' }}
                    />
                );
            }
            return (
                <div
                    className="d-flex align-items-center justify-content-center rounded-3 border border-secondary border-dashed flex-shrink-0 text-muted"
                    style={{ width: `${sideElementSize}px`, height: `${sideElementSize}px`, fontSize: '12px' }}
                >
                    <i className="bi bi-qr-code fs-3 text-warning"></i>
                </div>
            );
        }

        // Formats with image on left: 1, 3, 4, 10
        if ([1, 3, 4, 10].includes(currentFormatId)) {
            if (leftImageUrl) {
                return (
                    <img
                        src={resolveMediaUrl(leftImageUrl)}
                        alt="Logo Izquierdo"
                        className="rounded-3 shadow-sm object-fit-contain flex-shrink-0"
                        style={{ width: `${sideElementSize}px`, height: `${sideElementSize}px`, background: 'rgba(255,255,255,0.08)' }}
                    />
                );
            }
            if (leftQrUrl) {
                return (
                    <div className="d-flex align-items-center justify-content-center p-1 bg-white rounded-3 shadow-sm flex-shrink-0">
                        <QRCodeSVG value={leftQrUrl} size={qrPixelSize} level="M" />
                    </div>
                );
            }
            return (
                <div
                    className="d-flex align-items-center justify-content-center rounded-3 border border-secondary border-dashed flex-shrink-0 text-white-50"
                    style={{ width: `${sideElementSize}px`, height: `${sideElementSize}px` }}
                >
                    <i className="bi bi-image fs-3"></i>
                </div>
            );
        }

        return null;
    };

    const renderRightElement = () => {
        // Formats with QR on right: 7, 9
        if (currentFormatId === 7 || currentFormatId === 9) {
            if (rightQrUrl) {
                return (
                    <div className="d-flex align-items-center justify-content-center p-1 bg-white rounded-3 shadow-sm flex-shrink-0">
                        <QRCodeSVG value={rightQrUrl} size={qrPixelSize} level="M" />
                    </div>
                );
            }
            if (rightImageUrl) {
                return (
                    <img
                        src={resolveMediaUrl(rightImageUrl)}
                        alt="Derecha"
                        className="rounded-3 shadow-sm object-fit-contain flex-shrink-0"
                        style={{ width: `${sideElementSize}px`, height: `${sideElementSize}px`, background: 'rgba(255,255,255,0.08)' }}
                    />
                );
            }
            return (
                <div
                    className="d-flex align-items-center justify-content-center rounded-3 border border-secondary border-dashed flex-shrink-0 text-muted"
                    style={{ width: `${sideElementSize}px`, height: `${sideElementSize}px`, fontSize: '12px' }}
                >
                    <i className="bi bi-qr-code fs-3 text-warning"></i>
                </div>
            );
        }

        // Formats with image on right: 1, 3, 4, 11
        if ([1, 3, 4, 11].includes(currentFormatId)) {
            if (rightImageUrl) {
                return (
                    <img
                        src={resolveMediaUrl(rightImageUrl)}
                        alt="Logo Derecho"
                        className="rounded-3 shadow-sm object-fit-contain flex-shrink-0"
                        style={{ width: `${sideElementSize}px`, height: `${sideElementSize}px`, background: 'rgba(255,255,255,0.08)' }}
                    />
                );
            }
            if (rightQrUrl) {
                return (
                    <div className="d-flex align-items-center justify-content-center p-1 bg-white rounded-3 shadow-sm flex-shrink-0">
                        <QRCodeSVG value={rightQrUrl} size={qrPixelSize} level="M" />
                    </div>
                );
            }
            return (
                <div
                    className="d-flex align-items-center justify-content-center rounded-3 border border-secondary border-dashed flex-shrink-0 text-white-50"
                    style={{ width: `${sideElementSize}px`, height: `${sideElementSize}px` }}
                >
                    <i className="bi bi-image fs-3"></i>
                </div>
            );
        }

        return null;
    };

    // Render central texts based on layout format
    const renderCenterContent = () => {
        const textStyle = {
            color: textColor || '#FFFFFF',
            fontFamily: getFontFamilyStack(fontFamily),
            fontSize: `${effectiveFontSize}px`,
            fontWeight: fontFamily === 'Montserrat' ? 700 : 'bold',
            lineHeight: 1.15,
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.75)',
            letterSpacing: fontFamily === 'Bangers' ? '1.5px' : '0.5px',
            wordBreak: 'break-word',
            margin: 0
        };

        const subStyle = {
            color: subtitleColor || '#FCE68A',
            fontFamily: getFontFamilyStack(fontFamily),
            fontSize: `${effectiveSubtitleSize}px`,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            textShadow: '0 1px 4px rgba(0, 0, 0, 0.6)',
            opacity: 0.95,
            margin: 0
        };

        // Formats with NO subtitle (1, 2)
        if (currentFormatId === 1 || currentFormatId === 2) {
            return (
                <div className="d-flex flex-column justify-content-center align-items-center text-center px-2 flex-grow-1 overflow-hidden">
                    <p style={textStyle}>{mainText || 'Escribe un mensaje...'}</p>
                </div>
            );
        }

        // Formats with SUBTITLE ON TOP (3, 5, 7, 8, 9, 10, 11)
        if ([3, 5, 7, 8, 9, 10, 11].includes(currentFormatId)) {
            return (
                <div className="d-flex flex-column justify-content-center align-items-center text-center px-2 flex-grow-1 overflow-hidden">
                    {subtitleText && <p style={subStyle} className="mb-1">{subtitleText}</p>}
                    <p style={textStyle}>{mainText || 'Escribe un mensaje...'}</p>
                </div>
            );
        }

        // Formats with SUBTITLE ON BOTTOM (4, 6)
        if (currentFormatId === 4 || currentFormatId === 6) {
            return (
                <div className="d-flex flex-column justify-content-center align-items-center text-center px-2 flex-grow-1 overflow-hidden">
                    <p style={textStyle} className="mb-1">{mainText || 'Escribe un mensaje...'}</p>
                    {subtitleText && <p style={subStyle}>{subtitleText}</p>}
                </div>
            );
        }

        return (
            <div className="d-flex flex-column justify-content-center align-items-center text-center px-2 flex-grow-1 overflow-hidden">
                <p style={textStyle}>{mainText || 'Escribe un mensaje...'}</p>
            </div>
        );
    };

    // Inner Cintillo / Banner Markup
    const bannerContent = (
        <div
            className="fanmessage-preview-belt position-relative w-100 rounded-4 overflow-hidden d-flex align-items-center justify-content-between px-3 px-md-4"
            style={{
                ...getBackgroundStyle(),
                minHeight: `${bannerHeight}px`,
                transform: `translateY(${Number(offsetY) || 0}px)`,
                transition: 'transform 0.2s ease, all 0.3s ease'
            }}
        >
            {/* Video background if configured */}
            {backgroundType === 'video' && backgroundUrl && (
                <video
                    src={resolveMediaUrl(backgroundUrl)}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="position-absolute top-0 start-0 w-100 h-100 object-fit-cover"
                    style={{ zIndex: 0, opacity: 0.85 }}
                />
            )}

            {/* Ambient inner glow layer */}
            <div
                className="position-absolute top-0 start-0 w-100 h-100 pointer-events-none"
                style={{
                    background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.3) 100%)',
                    zIndex: 1
                }}
            />

            {/* Content layer */}
            <div className="position-relative w-100 d-flex align-items-center justify-content-between gap-2 gap-md-3" style={{ zIndex: 2 }}>
                {renderLeftElement()}
                {renderCenterContent()}
                {renderRightElement()}
            </div>
        </div>
    );

    if (!showContainerFrame) {
        return bannerContent;
    }

    return (
        <div className="fanmessage-stadium-preview-frame rounded-4 overflow-hidden border border-secondary shadow-lg bg-dark position-relative p-3 p-md-4">
            {/* Header simulated overlay bar */}
            <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom border-secondary-subtle">
                <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-danger text-uppercase px-2 py-1">
                        <i className="bi bi-broadcast me-1"></i> Live Preview
                    </span>
                    <span className="text-white-50 small">
                        Formato #{currentFormatId} - {FAN_MESSAGE_FORMATS.find(f => f.id === currentFormatId)?.label || 'Personalizado'}
                    </span>
                </div>
                <div className="small text-white-50">
                    Fuente: <strong className="text-warning">{fontFamily}</strong> ({fontSizePx}px)
                    {offsetY !== 0 && <span className="ms-2">Offset: <strong className="text-info">{offsetY}px</strong></span>}
                </div>
            </div>

            {/* Simulated stadium screen viewport */}
            <div
                className="position-relative w-100 rounded-3 d-flex flex-column justify-content-end p-3 overflow-hidden"
                style={{
                    minHeight: mode === 'compact' ? '120px' : '200px',
                    background: 'linear-gradient(rgba(10, 15, 29, 0.8), rgba(10, 15, 29, 0.95)), repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.02) 2px, rgba(255,255,255,0.02) 4px)'
                }}
            >
                {/* Stadium background watermark */}
                <div
                    className="position-absolute top-50 start-50 translate-middle opacity-10 pointer-events-none text-center"
                    style={{ width: '80%' }}
                >
                    <i className="bi bi-shield-fill-check text-white" style={{ fontSize: '80px' }}></i>
                </div>

                {bannerContent}
            </div>
        </div>
    );
};
